import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Search as SearchIcon, X, ArrowRight, FileText } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { textMatchesQuery } from '../utils/searchNormalize'
import { pathForServicesDataKey } from '../utils/serviceSearchPaths'

function stripHash(path) {
  if (!path || typeof path !== 'string') return path
  const i = path.indexOf('#')
  return i === -1 ? path : path.slice(0, i)
}

function collectMegaMenuItems(t, items, seen) {
  const mega = t('header.megaMenu', { returnObjects: true })
  if (!mega) return
  const sections = Array.isArray(mega) ? mega : Object.values(mega)
  sections.forEach((section) => {
    if (!section || typeof section !== 'object') return
    if (section.title && section.path) {
      const path = stripHash(section.path)
      const k = `mega|${path}|${section.title}`
      if (!seen.has(k)) {
        seen.add(k)
        items.push({
          type: 'nav',
          title: section.title,
          subtitle: '',
          parent: '',
          path,
        })
      }
    }
    ;(section.categories || []).forEach((cat) => {
      if (cat.title && cat.path) {
        const path = stripHash(cat.path)
        const k = `mega|${path}|${cat.title}`
        if (!seen.has(k)) {
          seen.add(k)
          items.push({
            type: 'nav',
            title: cat.title,
            subtitle: section.title || '',
            parent: section.title || '',
            path,
          })
        }
      }
      ;(cat.items || []).forEach((item) => {
        const label = typeof item === 'object' ? item.label : null
        const ipath = typeof item === 'object' ? item.path : null
        if (label && ipath) {
          const path = stripHash(ipath)
          const k = `mega|${path}|${label}`
          if (!seen.has(k)) {
            seen.add(k)
            items.push({
              type: 'nav',
              title: label,
              subtitle: cat.title || '',
              parent: cat.title || '',
              path,
            })
          }
        }
      })
    })
  })
}

const SearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('')
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()

  const searchableItems = useMemo(() => {
    const sd = t('servicesData', { returnObjects: true })
    const items = []
    const seen = new Set()

    if (sd && typeof sd === 'object') {
      Object.keys(sd).forEach((key) => {
        const service = sd[key]
        if (!service) return
        const path = pathForServicesDataKey(key)

        items.push({
          type: 'service',
          title: service.title,
          subtitle: service.subtitle,
          parent: '',
          path,
        })

        if (Array.isArray(service.modules)) {
          service.modules.forEach((module) => {
            items.push({
              type: 'module',
              title: module.title,
              subtitle: '',
              parent: service.title,
              path,
            })
            if (Array.isArray(module.tables)) {
              module.tables.forEach((table) => {
                if (table.heading) {
                  items.push({
                    type: 'heading',
                    title: table.heading,
                    subtitle: module.title || '',
                    parent: service.title,
                    path,
                  })
                }
              })
            }
          })
        }
      })
    }

    collectMegaMenuItems(t, items, seen)
    return items
  }, [t, i18n.language])

  const results = useMemo(() => {
    if (!query.trim()) return []
    return searchableItems
      .filter((item) => textMatchesQuery(query, item.title, item.subtitle, item.parent))
      .slice(0, 8)
  }, [query, searchableItems])

  const secondaryText = useCallback(
    (item) => {
      if (item.type === 'service') return item.subtitle || ''
      if (item.type === 'module') return t('search.serviceModule')
      if (item.type === 'nav') return item.subtitle || ''
      if (item.type === 'heading') return item.subtitle || item.parent || ''
      return ''
    },
    [t]
  )

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [onClose])

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-start justify-center pt-24 px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-text-main/40 backdrop-blur-sm"
          onClick={onClose}
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-border-light overflow-hidden"
        >
          <div className="p-4 border-b border-border-light flex items-center gap-3">
            <SearchIcon className="w-5 h-5 text-text-dim" />
            <input
              autoFocus
              type="text"
              placeholder={t('search.placeholder')}
              className="flex-1 bg-transparent border-none outline-none text-text-main placeholder:text-text-dim/60 text-lg"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button onClick={onClose} className="p-2 hover:bg-surface-100 rounded-xl transition-colors">
              <X className="w-5 h-5 text-text-dim" />
            </button>
          </div>

          <div className="max-h-[60vh] overflow-y-auto p-2">
            {results.length > 0 ? (
              <div className="space-y-1">
                {results.map((item, i) => (
                  <button
                    key={`${item.type}-${item.path}-${item.title}-${i}`}
                    onClick={() => {
                      navigate(item.path)
                      onClose()
                    }}
                    className="w-full flex items-center gap-4 p-4 rounded-2xl hover:bg-primary-50 group transition-all text-left"
                  >
                    <div className="w-10 h-10 rounded-xl bg-surface-100 group-hover:bg-white flex items-center justify-center text-text-dim group-hover:text-primary transition-colors">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-text-main">{item.title}</span>
                        {item.parent &&
                          (item.type === 'module' || item.type === 'nav' || item.type === 'heading') && (
                            <span className="text-[10px] font-bold uppercase tracking-wider text-text-dim bg-surface-100 px-1.5 py-0.5 rounded">
                              {item.parent}
                            </span>
                          )}
                      </div>
                      <p className="text-xs text-text-dim truncate mt-0.5">{secondaryText(item)}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-text-dim opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                  </button>
                ))}
              </div>
            ) : query.trim() ? (
              <div className="py-12 text-center">
                <p className="text-text-dim text-sm">{t('search.noResults', { query })}</p>
              </div>
            ) : (
              <div className="py-12 text-center">
                <p className="text-text-dim text-sm">{t('search.startTyping')}</p>
              </div>
            )}
          </div>

          <div className="p-4 bg-surface-50 border-t border-border-light flex justify-between items-center text-[10px] font-bold text-text-dim uppercase tracking-widest">
            <div className="flex gap-4">
              <span>
                <kbd className="bg-white border border-border-light px-1.5 py-0.5 rounded shadow-sm mr-1">ESC</kbd>{' '}
                {t('search.close')}
              </span>
              <span>
                <kbd className="bg-white border border-border-light px-1.5 py-0.5 rounded shadow-sm mr-1">↵</kbd>{' '}
                {t('search.select')}
              </span>
            </div>
            <span>{t('search.poweredBy')}</span>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default SearchModal
